import { List, ListItem, Typography } from '@material-ui/core';
import Link from 'next/link';
import { makeStyles } from '@material-ui/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'tools/i18n';
import DocumentIcon from '../../../public/images/icons/document.svg';
import Cookies from 'js-cookie';
import useSessionStore from '@services/stores/SessionStore';

const useStyles = makeStyles(theme => ({
    list: {
        marginTop: 15,
    },
    item: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: '3px 0',
        fontSize: 15,
        lineHeight: 1.75,
        [theme.breakpoints.down('sm')]: {
            fontSize: 13,
        },
    },
    label: {
        fontWeight: 300,
        lineHeight: 'inherit',
    },
    value: {
        maxWidth: '70%',
        fontSize: 'inherit',
        fontWeight: 300,
        color: theme.palette.primary.color,
        textAlign: 'right',
        lineHeight: 'inherit',
        '& > p': {
            fontWeight: 300,
            fontSize: 15,
            color: theme.palette.primary.color,
            [theme.breakpoints.down('sm')]: {
                fontSize: 13,
            },
        },
        '&.manufacturer': {
            whiteSpace: 'nowrap',
        },
    },
    measurementImage: {
        display: 'block',
        margin: '0 auto',
        padding: '20px 20px',
        marginTop: 20,
        maxHeight: 450,
        maxWidth: '90%',
        [theme.breakpoints.down('sm')]: {
            padding: '20px 0px',
            maxHeight: 400,
        },
        '&.horizontal': {
            [theme.breakpoints.up('md')]: {
                fontSize: 32,
            },
        },
        '&.generic-armchair': {
            maxHeight: 350,
        },
    },
}), { name: 'ProductSpecs' });

export default function ProductSpecs({ product, productFamilyArticle }) {
    const classes = useStyles();
    const { t } = useTranslation('attributes');
    const formatNumber = useSessionStore((state) => state.methods.formatNumber);
    const {
        salesChannel,
        config: {
            language: { locale },
        },
    } = useSessionStore((state) => state.session);
    const specAttributes = salesChannel?.customFields?.interstellarAttributes || [];
    const properties = product.properties || [];
    const customFields = product?.translated?.customFields || product?.customFields;
    const [ isInternal, setIsInternal ] = useState(false);

    useEffect(() => {
        let internalCookie = Cookies.get('InternalTools');

        if (internalCookie) {
            setIsInternal(true);
        }
    }, []);

    function getMeasurements(sketch) {
        const legacySize = customFields?.legacy_size;
        const attributes = Object.values(customFields?.attributes || {});

        const legacyMeasurement = [
            {
                label: t('attributes:legacy_size'),
                value: legacySize,
                group: 'measurements',
            },
        ];

        const measurements = [];
        attributes.forEach(function(item) {
            const group = item.group.split(':');
            if (group[0] !== 'measurements') {
                return;
            }

            if (group[1] === 'mm') {
                return measurements.push({
                    label: item.label,
                    value: formatNumber((item.value / 10).toFixed(2)) + ' cm',
                    group: item.group,
                });
            }

            if (item.label?.includes('(kg)') || group[1] === 'kg') {
                let weight = parseFloat(item.value);
                let unit = 'kg';

                if (weight >= 1) {
                    weight = formatNumber((weight).toFixed(2));
                }

                if (weight < 1) {
                    weight = formatNumber((weight * 1000).toFixed(0));
                    unit = 'g';
                }

                return measurements.push({
                    label: item.label.replace('(kg)', ''),
                    value: `${weight} ${unit}`,
                    group: item.group,
                });
            }

            return measurements.push({
                label: item.label,
                value: formatNumber((item.value).toFixed(2)),
                group: item.group,
            });
        });

        if (legacySize && (measurements.length < ((legacySize.match(/x/g))?.length + 1))
            || legacySize && measurements.length === 0
        ) {
            return legacyMeasurement;
        }

        return measurements.sort(function(a, b) {
            const textA = a?.label?.toUpperCase();
            const textB = b?.label?.toUpperCase();
            return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
        });

    }

    function getProductSpecs() {
        let attributes = [];

        attributes.push({
            label: t('product-info:article-number'),
            value: product.productNumber,
        });

        specAttributes.forEach((item) => {
            const key = item.code;
            if (customFields[key]) {
                if (getProperty(item)) {
                    attributes.push(getProperty(item));
                } else if (key === 'legacy_size' || (locale === 'sv' || locale === 'fi' || locale === 'no')) {
                    attributes.push({
                        label: t('attributes:' + key),
                        value: product.customFields[key],
                        code: key,
                        group: 'customField',
                    });
                }
                return;
            }

            const attribute = getProperty(item);
            if (attribute) {
                if (attribute.group === 'color') {
                    const existing = attributes.find((item) => item.group === 'color');
                    if (existing) {
                        if (existing.value && existing.value?.includes(attribute.value)) {
                            return;
                        }
                        existing.value = `${existing.value} / ${attribute.value}`;
                        return;
                    }
                }

                attributes.push(attribute);
            }
        });

        const customAttributes = Object.values(customFields?.attributes || {}).filter(item => !item.group?.includes('measurements'));
        customAttributes.forEach(item => {
            attributes.push(
                {
                    label: item.label,
                    value: item.value,
                    group: item.group,
                },
            );
        });

        return attributes.sort(function(a, b) {
            const textA = a?.label?.toUpperCase();
            const textB = b?.label?.toUpperCase();
            return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
        });
    }

    function getProperty(item) {
        let groupName = '';
        let groupValues = properties.filter((property) => {
            return property.groupId === item.id;
        }).map((property) => {
            groupName = property.group.translated.name;
            return property.translated.name;
        });

        if (!groupValues || groupValues.length === 0) {
            return null;
        }

        return {
            label: groupName,
            value: groupValues.join(' / '),
            code: item.code,
            group: item.group,
        };
    }

    function getManufacturer() {
        const manufacturer = product.manufacturer;
        return manufacturer ? manufacturer.translated.name : false;
    }

    function getProductDocuments() {
        let media = product?.media;
        let documents = [];
        let documentCount = 1;

        if (!media) {
            return documents;
        }

        media.forEach((item, index) => {
            let customFields = item.media.translated.customFields;

            if (customFields.display_types?.indexOf('document') >= 0) {
                let fileName = customFields.title && customFields.title[locale] ? customFields.title[locale] : customFields.title['en'] || false;

                if (fileName) {
                    documents.push({
                        fileName,
                        url: item.media.url,
                    });

                    documentCount++;
                }
            }
        });

        return documents;
    }

    function getMeasurementSketch() {
        let measurementProperty = properties.filter((property) => {
            return property.group?.translated?.customFields?.attributeCode === 'measurement_sketch';
        });

        if (!measurementProperty || measurementProperty.length === 0) {
            return false;
        }

        let property = measurementProperty[0];

        if (property) {
            let url = '/images/sketches/';

            return <img alt={'Unique Tag'} src={url + property?.translated?.customFields?.uniqueTag}
                        className={classes.measurementImage} />;
        } else {

        }
    }

    function getManufacturerLink() {
        const manufacturer = product.manufacturer;

        return manufacturer.link || false;
    }

    const measurementSketch = getMeasurementSketch();
    const measurements = getMeasurements(measurementSketch).map(({ label, value }, index) => {
        return (
            <ListItem className={classes.item} key={index} disableGutters>
                <label className={classes.label}>{label}</label>
                <Typography className={classes.value} variant="body1">{value}</Typography>
            </ListItem>
        );
    });
    const attributes = getProductSpecs();
    const materialGroup = attributes.filter((item) => item.group === 'material').map(({ label, value }, index) => {
        return (
            <ListItem className={classes.item} key={index} disableGutters>
                <label className={classes.label}>{label}</label>
                <Typography className={classes.value} variant="body1">{value}</Typography>
            </ListItem>
        );
    });

    const defaultGroup = attributes.filter((item) => item.group !== 'material').map(({ label, code, value }, index) => {
        let link = false;
        if (code && code === 'product_family' && productFamilyArticle) {
            link = '/' + productFamilyArticle?.seoUrls[0].seoPathInfo;
        }

        return (
            <ListItem className={classes.item} key={index} disableGutters>
                <label className={classes.label}>{label}</label>
                {link ?
                    <Link href={link}><a className={classes.value}><Typography variant="body1">{value}</Typography></a></Link> :
                    <Typography className={classes.value} variant="body1">{value}</Typography>}
            </ListItem>
        );
    });

    const internalList = () => {
        return (
            <ListItem style={{ marginTop: '1rem' }} className={[ classes.item, 'internal' ].join(' ')}
                      key="internalTool" disableGutters>
                <label className={classes.label}><b>Internal</b></label>
                <Link href={'https://interstellar' + '.newport.se/' + 'products/' + product.productNumber}><a
                    className={classes.value}><Typography variant="body1">Grey link</Typography></a></Link>
            </ListItem>
        );
    };

    return (
        <>
            <List className={classes.list} disablePadding>
                {getManufacturer() ?
                    <ListItem className={classes.item} disableGutters>
                        <label className={classes.label}>{t('attributes:manufacturer')}</label>
                        {getManufacturerLink() ? <Link href={getManufacturerLink()}><a><Typography
                                className={[ classes.value, 'manufacturer' ].join(' ')}
                                variant="body1">{getManufacturer()}</Typography></a></Link> :
                            <Typography className={classes.value} variant="body1">{getManufacturer()}</Typography>}
                    </ListItem>
                    : ''}

                {defaultGroup}

                {getProductDocuments().length > 0 ?
                    <ListItem className={classes.item} disableGutters>
                        <label className={classes.label}>{t('product-info:product-document-label')}</label>
                        <Typography className={classes.value} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
                                    variant="body1">
                            {getProductDocuments().map((document, index) => {
                                return (
                                    <a href={document.url} key={index}
                                       style={{ display: 'flex', alignItems: 'center', color: 'inherit' }}>
                                        <DocumentIcon style={{ width: 20, marginRight: 2 }} />
                                        {document.fileName}
                                    </a>
                                );
                            })}
                        </Typography>
                    </ListItem>
                    : ''}

                {materialGroup.length > 1 ?
                    <ListItem style={{ marginTop: '1rem' }} className={classes.item} key="material" disableGutters>
                        <label className={classes.label}><b>{t('attributes:material')}</b></label>
                        <Typography className={classes.value} variant="body1"></Typography>
                    </ListItem> : ''}
                {materialGroup}

                {measurements.length > 1 ?
                    <ListItem style={{ marginTop: '1rem' }} className={classes.item} key="measurements" disableGutters>
                        <label className={classes.label}><b>{t('attributes:measurements')}</b></label>
                        <Typography className={classes.value} variant="body1"></Typography>
                    </ListItem> : ''}
                {measurements}

                {measurementSketch ? measurementSketch : ''}

                {isInternal ? internalList() : ''}
            </List>
        </>
    );
}
